import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import useElementWidth from "../../../hooks/useElementWidth";
import "./SeriesChart.scss";

const M = { top: 10, right: 46, bottom: 24, left: 4 };
const defaultFormat = (v) => Number(v || 0).toLocaleString("en-GB");

/**
 * SeriesChart — several series over the same x axis, three ways:
 *
 *   variant="stacked"  areas stacked: the total over time AND what it was made of
 *   variant="share"    the same, each day scaled to 100%: how the MIX moved, whatever the total did
 *   variant="lines"    one line each, not stacked — for series that are compared, not summed
 *                      (they may be different lengths: a curve can stop at today)
 *   variant="bars"     stacked COLUMNS, one a label: for a few whole periods (months), where an
 *                      area between two or three points says nothing (Oct 2026, System Health's
 *                      cost by month). A label whose values are all 0 has no column.
 *
 * Drawn in real pixels from the measured width, so type stays legible. Hovering shows
 * every series for that point; hovering the legend isolates one.
 *
 * @param series     [{ key, label, color, values: number[] }] — index-aligned with `labels`
 * @param labels     x-axis labels (days, weeks…)
 * @param totalText  optional (index, total) → the words for the hover's total line, or null for
 *                   none. For a caller whose 0 can mean "nothing known" rather than nought: System
 *                   Health's cost by month says "Nothing imported for this month", never "Total
 *                   £0.00" (Oct 2026). Without it the line is "Total <value>", as before.
 */
const SeriesChart = ({
  series, labels, variant = "stacked", height = 220, formatValue = defaultFormat, labelFormatter = (l) => l,
  unit, showLegend = true, emptyText = "Nothing in this period.", xTitle, totalText,
}) => {
  const [ref, width] = useElementWidth(640);
  const [hover, setHover] = useState(null);
  const [focus, setFocus] = useState(null);
  const n = labels.length;
  const live = (series || []).filter((s) => s.values.some((v) => v));

  const geo = useMemo(() => {
    if (!live.length || !n || !width) return null;
    const w = width - M.left - M.right;
    const h = height - M.top - M.bottom;
    const bars = variant === "bars";
    const band = w / n; // bars: each label owns one band, and its column sits in the middle
    const barW = Math.max(4, Math.min(band * 0.62, 56));
    const x = (i) => (bars ? M.left + band * (i + 0.5) : M.left + (n > 1 ? (i / (n - 1)) * w : w / 2));
    const totals = labels.map((_, i) => live.reduce((t, s) => t + (s.values[i] || 0), 0));
    const max = variant === "share" ? 1
      : variant === "lines" ? Math.max(1, ...live.flatMap((s) => s.values.map((v) => v || 0)))
        : Math.max(1, ...totals);
    const y = (v) => M.top + h - (v / max) * h;
    const val = (s, i) => {
      const v = s.values[i] || 0;
      return variant === "share" ? (totals[i] ? v / totals[i] : 0) : v;
    };
    const shapes = [];
    if (bars) {
      const base = labels.map(() => 0);
      live.forEach((s) => {
        const rects = [];
        labels.forEach((_, i) => {
          const v = s.values[i] || 0;
          if (v <= 0) return;
          const lower = base[i];
          base[i] += v;
          rects.push({ i, x: x(i) - barW / 2, y: y(base[i]), h: Math.max(1, y(lower) - y(base[i])) });
        });
        shapes.push({ key: s.key, color: s.color, rects });
      });
    } else if (variant === "lines") {
      live.forEach((s) => {
        const pts = s.values.map((v, i) => `${x(i).toFixed(1)},${y(v || 0).toFixed(1)}`);
        shapes.push({ key: s.key, color: s.color, line: pts.join(" "), end: s.values.length - 1, endY: y(s.values[s.values.length - 1] || 0) });
      });
    } else {
      const base = labels.map(() => 0);
      live.forEach((s) => {
        const lower = [...base];
        labels.forEach((_, i) => { base[i] += val(s, i); });
        const top = labels.map((_, i) => `${x(i).toFixed(1)},${y(base[i]).toFixed(1)}`);
        const bottom = labels.map((_, i) => `${x(i).toFixed(1)},${y(lower[i]).toFixed(1)}`).reverse();
        shapes.push({ key: s.key, color: s.color, area: `M${top.join(" L")} L${bottom.join(" L")} Z`, line: top.join(" ") });
      });
    }
    const step = n <= 8 ? 1 : Math.ceil(n / (width < 520 ? 4 : 7));
    const ticks = labels.map((l, i) => ({ i, l })).filter(({ i }) => i % step === 0 || i === n - 1)
      .filter(({ i }, k, arr) => !(k === arr.length - 2 && n - 1 - i < step * 0.75)); // no crowding at the end
    return { w, h, x, y, max, totals, shapes, ticks, val, bars, band, barW };
  }, [live, labels, n, width, height, variant]);

  const onMove = (e) => {
    if (!geo) return;
    const box = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - box.left - M.left;
    const i = geo.bars ? Math.floor(px / geo.band) : Math.round((px / geo.w) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const fmtAxis = (v) => (variant === "share" ? `${Math.round(v * 100)}%` : formatValue(v));

  return (
    <div className="series-chart" ref={ref}>
      {!geo ? <p className="series-chart__empty">{emptyText}</p> : (
        <>
          <div className="series-chart__plot" style={{ height }}>
            <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img"
              aria-label={`${live.map((s) => s.label).join(", ")} ${variant === "share" ? "as shares" : "over time"}`}>
              {[0, 0.5, 1].map((f) => (
                <g key={f}>
                  <line x1={M.left} x2={M.left + geo.w} y1={geo.y(geo.max * f)} y2={geo.y(geo.max * f)} className="series-chart__grid" />
                  <text x={M.left + geo.w + 6} y={geo.y(geo.max * f)} dominantBaseline="middle" className="series-chart__tick">{fmtAxis(geo.max * f)}</text>
                </g>
              ))}
              {geo.bars && hover != null && (
                <rect x={M.left + geo.band * hover} y={M.top} width={geo.band} height={geo.h} className="series-chart__band" />
              )}
              {geo.shapes.map((s) => {
                const dim = focus && focus !== s.key;
                if (s.rects) {
                  return (
                    <g key={s.key} style={{ opacity: dim ? 0.15 : 1 }} className="series-chart__series">
                      {s.rects.map((r) => (
                        <rect key={r.i} x={r.x} y={r.y} width={geo.barW} height={r.h} fill={s.color} className="series-chart__bar" />
                      ))}
                    </g>
                  );
                }
                return (
                  <g key={s.key} style={{ opacity: dim ? 0.15 : 1 }} className="series-chart__series">
                    {s.area && <path d={s.area} fill={s.color} className="series-chart__area" />}
                    <polyline points={s.line} fill="none" stroke={s.color} className={s.area ? "series-chart__edge" : "series-chart__line"} />
                    {!s.area && <circle cx={geo.x(s.end)} cy={s.endY} r={3.5} fill={s.color} />}
                  </g>
                );
              })}
              {geo.ticks.map(({ i, l }) => (
                <text key={i} x={geo.x(i)} y={height - 6} textAnchor={geo.bars ? "middle" : i === 0 ? "start" : i === n - 1 ? "end" : "middle"} className="series-chart__tick">
                  {labelFormatter(l)}
                </text>
              ))}
              {xTitle && <text x={M.left} y={M.top + 2} className="series-chart__x-title" dominantBaseline="hanging">{xTitle}</text>}
              {hover != null && !geo.bars && <line x1={geo.x(hover)} x2={geo.x(hover)} y1={M.top} y2={M.top + geo.h} className="series-chart__guide" />}
            </svg>
            {hover != null && (
              <div className="series-chart__tip" style={{ left: Math.min(geo.x(hover) + 12, width - 190), top: M.top }}>
                <strong>{labelFormatter(labels[hover])}</strong>
                {live.filter((s) => (s.values[hover] || 0) > 0).slice().reverse().map((s) => (
                  <span key={s.key}>
                    <i style={{ background: s.color }} />
                    {s.label}
                    <b>{formatValue(s.values[hover])}{variant === "share" && geo.totals[hover] ? ` · ${Math.round(geo.val(s, hover) * 100)}%` : ""}</b>
                  </span>
                ))}
                {variant !== "lines" && (totalText
                  ? (() => { const t = totalText(hover, geo.totals[hover]); return t ? <em>{t}</em> : null; })()
                  : <em>Total {formatValue(geo.totals[hover])}{unit ? ` ${unit}` : ""}</em>)}
              </div>
            )}
          </div>
          {showLegend && (
            <div className="series-chart__legend">
              {live.map((s) => (
                <button type="button" key={s.key} className={`series-chart__key${focus === s.key ? " series-chart__key--on" : ""}`}
                  onMouseEnter={() => setFocus(s.key)} onMouseLeave={() => setFocus(null)} onFocus={() => setFocus(s.key)} onBlur={() => setFocus(null)}>
                  <i style={{ background: s.color }} />{s.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

SeriesChart.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired, label: PropTypes.string.isRequired, color: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.number).isRequired,
  })).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  variant: PropTypes.oneOf(["stacked", "share", "lines", "bars"]),
  height: PropTypes.number,
  formatValue: PropTypes.func,
  labelFormatter: PropTypes.func,
  unit: PropTypes.string,
  showLegend: PropTypes.bool,
  emptyText: PropTypes.string,
  xTitle: PropTypes.string,
  totalText: PropTypes.func,
};

export default SeriesChart;
